import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import styles from './BarChart.module.css';

const BarChart = ({ data,className }) => {
  const containerRef = useRef();
  const svgRef = useRef();
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          setWidth(entry.contentRect.width);
        }
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!data || data.length === 0 || width === 0) return;

    const margin = { top: 20, right: 20, bottom: 30, left: 40 };
    const height = 200 - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const defs = svg.append('defs');
    defs.append('linearGradient')
      .attr('id', 'blue-gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%')
      .selectAll('stop')
      .data([
        { offset: '0%', color: '#0064F9' },
        { offset: '100%', color: '#1e3a8a' },
      ])
      .enter()
      .append('stop')
      .attr('offset', d => d.offset)
      .attr('stop-color', d => d.color);

    const chart = svg
      .attr('width', width)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)
      .attr('class', styles.chart);

    const innerWidth = width - margin.left - margin.right;

    const x = d3
      .scaleBand()
      .domain(data.map((d) => d.modelName))
      .range([0, innerWidth])
      .padding(0.2);

    const y = d3
      .scaleLinear()
      .domain([0, 1])
      .range([height, 0]);

    chart
      .append('g')
      .attr('transform', `translate(0, ${height})`)
      .attr('class', styles.axisX)
      .call(d3.axisBottom(x))
      .call((g) => g.select('.domain').attr('stroke', '#999'))
      .call((g) => g.selectAll('.tick line').remove());

    const yTicks = [0.25, 0.5, 0.75, 1];
    chart
      .selectAll('line.' + styles.gridLine)
      .data(yTicks)
      .enter()
      .append('line')
      .attr('class', styles.gridLine)
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d));

    chart.append('g')
      .selectAll('text')
      .data([0, 1])
      .enter()
      .append('text')
      .attr('x', -10)
      .attr('y', (d) => y(d) + 4)
      .attr('text-anchor', 'end')
      .attr('font-size', '12px')
      .attr('fill', 'white')
      .text((d) => d);

    chart
      .selectAll('rect.' + styles.bar)
      .data(data)
      .enter()
      .append('rect')
      .attr('class', styles.bar)
      .attr('x', (d) => x(d.modelName))
      .attr('y', (d) => y(d.accuracy))
      .attr('width', x.bandwidth())
      .attr('height', (d) => height - y(d.accuracy))
      .attr('fill', 'url(#blue-gradient)')
      .attr('rx', 4);

    chart
      .selectAll('text.' + styles.barLabel)
      .data(data)
      .enter()
      .append('text')
      .attr('class', styles.barLabel)
      .attr('x', (d) => x(d.modelName) + x.bandwidth() / 2)
      .attr('y', (d) => y(d.accuracy) + 15)
      .attr('text-anchor', 'middle')
      .text((d) => `${(d.accuracy * 100).toFixed(1)}%`);
  }, [data, width]);

  return (
    <div 
    ref={containerRef} 
    style={{ width: '100%' }}
    className={`${styles['wrap']} ${className}`}>
        <header>Accuracy</header>
        <svg ref={svgRef} className={styles.svg}></svg>
    </div>
  );
};

export default BarChart;